import { useContext } from 'react';
import { CheckoutContextCoCo } from '../../context/CheckoutContextCoCo.js';
import { ContactOrPurposeWrapper } from '../../steps/products/ContactOrPurposeWrapper.js';
import { resetCoCoMainFormOrderItemUnits } from '../../utils/placeOrderUtilsCoCo.js';
import { schemaToCharacteristicFlags } from '../useProductConfigSchemaCoCo.js';
import type { ContactsUpdateMethods, PlaceOrderItemUnit } from '../../placeOrderTypes.js';
import type { PlaceOrderItemUnitCoCo } from '../../placeOrderTypesCoCo.js';
import type { ContactOrPurposeHandle } from '../../steps/products/PlaceOrderContactOrPurpose/PlaceOrderContactOrPurpose.js';
import type { ProductConfigSchema } from '../useProductConfigSchemaCoCo.js';
import type { ReactNode, RefObject } from 'react';

import './ContactOrPurposeSectionCoCo.scss';

export interface ContactOrPurposeSectionProps {
  schema: ProductConfigSchema;
  orderItem: PlaceOrderItemUnitCoCo;
  contactFormRefs: RefObject<Record<string, ContactOrPurposeHandle | null>>;
  contactsUpdateMethods: ContactsUpdateMethods;
  isMandatory?: boolean;
  isCostCenterRequired?: boolean;
  showAccordion?: boolean;
  defaultExpanded?: boolean;
  /** When false, no user (contact) information is collected for the product. */
  showUserContact?: boolean;
  /** Overrides for the catalogue derived field visibility. */
  showPurposeOfUse?: boolean;
  showCostCenter?: boolean;
  showReference?: boolean;
  /** Other order item units that are configured by this same section. */
  accessorySharedUnitKeys?: string[];
  contentBeforePurposeOfUse?: ReactNode;
  contentBeforeCostCenter?: ReactNode;
}

export const ContactOrPurposeSectionCoCo = ({
  schema,
  orderItem,
  contactFormRefs,
  contactsUpdateMethods,
  isMandatory = true,
  isCostCenterRequired = false,
  showAccordion = true,
  defaultExpanded,
  showUserContact = true,
  showPurposeOfUse,
  showCostCenter,
  showReference,
  accessorySharedUnitKeys,
  contentBeforePurposeOfUse,
  contentBeforeCostCenter,
}: ContactOrPurposeSectionProps) => {
  const schemaFields = schemaToCharacteristicFlags(schema).fieldsToShow;
  // EOE checkout does not collect user or purpose of use, even when the catalogue asks for them
  const isEmployeeOrder = useContext(CheckoutContextCoCo)?.isEmployeeOrder ?? false;
  const characteristicFlags = {
    fieldsToShow: {
      showPurposeOfUse: !isEmployeeOrder && (showPurposeOfUse ?? schemaFields.showPurposeOfUse),
      showCostCenter: showCostCenter ?? schemaFields.showCostCenter,
      showReference: showReference ?? schemaFields.showReference,
      showUserContact: showUserContact && !isEmployeeOrder,
    },
  };
  const { fieldsToShow } = characteristicFlags;

  if (
    !fieldsToShow.showUserContact &&
    !fieldsToShow.showPurposeOfUse &&
    !fieldsToShow.showCostCenter &&
    !fieldsToShow.showReference
  ) {
    return null;
  }

  const refId = `${orderItem.basketItemId}-${orderItem.basketItemIndex}`;

  return (
    <div className="of-contact-or-purpose-coco">
      <ContactOrPurposeWrapper
        showAccordion={showAccordion}
        defaultExpanded={defaultExpanded ?? isMandatory}
        isMandatory={isMandatory}
        isCostCenterRequired={isCostCenterRequired}
        contactFormRefs={contactFormRefs}
        orderItem={orderItem as unknown as PlaceOrderItemUnit}
        refId={refId}
        contactsUpdateMethods={contactsUpdateMethods}
        characteristicFlags={characteristicFlags}
        accessorySharedUnitKeys={accessorySharedUnitKeys}
        contentBeforePurposeOfUse={contentBeforePurposeOfUse}
        contentBeforeCostCenter={contentBeforeCostCenter}
        resetOrderItemUnits={resetCoCoMainFormOrderItemUnits}
      />
    </div>
  );
};
